/**
 * AI Models — kies welk OpenAI model elke feature aanroept.
 */
import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { SettingField, SectionHeader } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

// Resterend ElevenLabs-tegoed (incident 12 okt 2026: op zonder dat iemand het wist).
function ElevenLabsTegoed({ tegoed }) {
  if (!tegoed) return null;
  if (!tegoed.ok) {
    return (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        Tegoed niet uit te lezen: {tegoed.detail || 'onbekend'}
      </Typography>
    );
  }
  const pct = Math.round((tegoed.fractieResterend || 0) * 100);
  const laag = pct < 15 || (tegoed.dagenOver != null && tegoed.dagenOver < 3);
  const nl = (n) => Number(n || 0).toLocaleString('nl-NL');
  const reset = tegoed.resetOp
    ? new Date(tegoed.resetOp).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })
    : null;
  return (
    <Typography variant="caption" sx={{ display: 'block', color: laag ? 'error.main' : 'text.secondary' }}>
      {laag ? '⚠️' : '💳'} Tegoed: {nl(tegoed.resterend)} van {nl(tegoed.limiet)} credits over ({pct}%)
      {tegoed.dagenOver != null ? ` · ~${tegoed.dagenOver} dagen bij huidig verbruik` : ''}
      {reset ? ` · aangevuld op ${reset}` : ''}
    </Typography>
  );
}

// Verbinding-check voor de STT-providers (fal + ElevenLabs) — test de keys op
// déze omgeving zonder een echte opname te hoeven maken.
function SttStatusCheck() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/recorder/stt-status`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setResult(d);
    } catch (e) {
      setResult({ error: e.message });
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Button size="small" variant="outlined" onClick={run} disabled={busy}
        startIcon={busy ? <CircularProgress size={14} /> : null}
        sx={{ textTransform: 'none', fontWeight: 700 }}>
        Test STT-verbinding
      </Button>
      {result && (
        <Box sx={{ mt: 1 }}>
          {result.error ? (
            <Typography variant="caption" color="error">Check mislukt: {result.error}</Typography>
          ) : (
            <>
              <Typography variant="caption" sx={{ display: 'block' }}>
                {result.fal?.keyPresent ? '✅' : '❌'} fal.ai — key {result.fal?.keyPresent ? 'aanwezig' : 'ontbreekt'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                {result.elevenlabs?.ok ? '✅' : '❌'} ElevenLabs — {result.elevenlabs?.ok
                  ? `key + Speech to Text-scope werken (HTTP ${result.elevenlabs.status})`
                  : (result.elevenlabs?.detail || 'key ontbreekt of ongeldig')}
              </Typography>
              <ElevenLabsTegoed tegoed={result.elevenlabs?.tegoed} />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// Maandelijkse modelcheck (Joris, 10 sep 2026): welk model draait welke tool,
// wat is er nieuw bij de aanbieders, en waar kan het beter. De ronde mailt zelf
// naar Joris en Ton; hier kun je hem bekijken zonder te mailen.
function Modelcheck() {
  const [bezig, setBezig] = useState(false);
  const [rapport, setRapport] = useState(null);
  const [fout, setFout] = useState(null);

  const bekijk = async () => {
    setBezig(true); setFout(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/modelcheck`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      setRapport(d);
    } catch (e) { setFout(e.message); } finally { setBezig(false); }
  };

  const ernstKleur = { dringend: '#b91c1c', 'let op': '#b45309', kans: '#166534' };

  return (
    <Box sx={{ mt: 4 }}>
      <SectionHeader
        title="Modelcheck"
        subtitle="Elke maand gaat er automatisch een overzicht naar Joris en Ton: welk model draait welke tool, wat is er nieuw, en waar kan het beter."
      />
      <Button size="small" variant="outlined" onClick={bekijk} disabled={bezig} sx={{ textTransform: 'none' }}>
        {bezig ? <CircularProgress size={14} /> : 'Bekijk de huidige stand'}
      </Button>
      {fout && <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>{fout}</Typography>}
      {rapport && (
        <Box sx={{ mt: 2 }}>
          {(rapport.signalen || []).length === 0 ? (
            <Typography variant="body2" sx={{ mb: 1 }}>Geen signalen — alles wat we gebruiken bestaat nog en is actueel.</Typography>
          ) : (
            <Box component="ul" sx={{ pl: 2.5, mb: 1.5 }}>
              {rapport.signalen.map((v, i) => (
                <Typography component="li" variant="body2" key={`${v.tool}-${i}`}>
                  <b style={{ color: ernstKleur[v.ernst] || 'inherit' }}>{v.ernst}</b> — {v.tool}: {v.tekst}
                </Typography>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(120px, auto) 1fr', gap: 0.5, fontSize: 13 }}>
            {(rapport.tools || []).map((t) => (
              <React.Fragment key={t.tool}>
                <Typography variant="body2">{t.tool}</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{t.model}</Typography>
                <Typography variant="body2" color="text.secondary">{t.bron}</Typography>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Tot 1 okt 2026 hardgecodeerd in de engine; nu instelbaar (modelcheck: 'maak alles instelbaar').
// Leeg laten of Reset = de standaard.
const OVERIGE_MODELLEN = [
  { key: 'flow_documents_model', label: 'Projectflow-documenten & Fable-analyses', help: 'Debrief, concept, briefing-check, dossiers, typering — Claude-model', standaard: 'claude-fable-5-1' },
  { key: 'typering_fallback_model', label: 'Fable-analyses — OpenAI-terugval', help: 'Als Anthropic niet kan (typering, branche, content-type, eindklant)', standaard: 'gpt-5.5' },
  { key: 'explainer_openai_model', label: 'Explainer Studio (OpenAI)', help: 'Script herschrijven naar explainer-stijl', standaard: 'gpt-5.5' },
  { key: 'explainer_claude_model', label: 'Explainer Studio (Claude)', help: 'Zelfde taak via Anthropic', standaard: 'claude-sonnet-5-5' },
  { key: 'projectflow_raad_model', label: 'Projectflow — fase raden', help: 'Bestandsnaam naar projectfase', standaard: 'gpt-5.5' },
  { key: 'offerte_products_model', label: 'Offertes — productcatalogus', help: 'NL↔EN-varianten koppelen en vertalen; houd dit snel', standaard: 'gpt-5-mini' },
  { key: 'overdracht_model', label: 'Overdracht', help: 'Dossier samenvatten en bijwerken', standaard: 'gpt-5-mini' },
  { key: 'slim_uren_model', label: 'Slim Uren — regels bijsturen', help: 'Koppelregels aanpassen via de chat', standaard: 'gpt-5.5' },
  { key: 'taak_toewijzing_model', label: 'Slimme toewijzing', help: 'Vrije vraag naar capaciteitszoekopdracht', standaard: 'gpt-5.5' },
  { key: 'account_plans_model', label: 'Accountplannen — import', help: 'Accountplan uit een document halen', standaard: 'gpt-5-mini' },
  { key: 'gpt_builder_model', label: 'GPT-maker', help: 'Gesprek, experts, domeinprofiel en spec; ook het model van nieuwe GPT\'s', standaard: 'gpt-5.4' },
  { key: 'gpt_builder_mini_model', label: 'GPT-maker — bronnen', help: 'Bronnen zoeken, scoren en samenvatten', standaard: 'gpt-5.4-mini' },
  { key: 'gpt_builder_transcribe_model', label: 'GPT-maker — transcriptie', help: 'Spraak-naar-tekst voor bronvideo\'s', standaard: 'whisper-1' },
  { key: 'hour_suggestions_model', label: 'Urenvoorstellen', help: 'Afspraken aan projecten koppelen', standaard: 'gpt-4o-mini' },
  { key: 'project_classify_model', label: 'Projectclassificatie', help: 'Type, doel en flags uit projecttekst', standaard: 'gpt-4o-mini' },
  { key: 'sales_ai_model', label: 'Sales — tips en mails', help: 'Claude-model', standaard: 'claude-sonnet-4-6' },
  { key: 'srt_uk_model', label: 'Ondertitels — Brits Engels', help: 'Omzetten naar Brits Engels', standaard: 'gpt-4o-mini' },
  { key: 'srt_translate_model', label: 'Ondertitels — vertalen', help: 'Vertalen naar een andere taal', standaard: 'gpt-5.4' },
  { key: 'video_search_model', label: 'Videotheek — categoriseren', help: 'Video in één categorie indelen', standaard: 'gpt-4o-mini' },
  { key: 'video_vision_model', label: 'Videotheek — beeldanalyse', help: 'Keyframes beschrijven (moet beelden kunnen lezen)', standaard: 'gpt-4o' },
  { key: 'video_transcribe_model', label: 'Videotheek — transcriptie', help: 'Spraak-naar-tekst', standaard: 'gpt-4o-mini-transcribe' },
  { key: 'video_qa_format_model', label: 'Edit QA — transcript opmaken', help: 'Sprekers en alinea\'s toevoegen', standaard: 'gpt-4o-mini' },
  { key: 'project_insight_model', label: 'Projectinzichten', help: 'Inzichten uit berichten halen', standaard: 'gpt-4o-mini' },
  { key: 'meetings_model', label: 'Meetings — samenvatting en vragen', help: 'Samenvatting, titel en vragen over een meeting', standaard: 'gpt-4o-mini' },
  { key: 'avatar_tts_model', label: 'Avatar — spraak', help: 'Tekst naar spraak', standaard: 'tts-1' },
];

export default function AIModels() {
  return (
    <Box>
      <SectionHeader
        title="AI modellen"
        subtitle="Configureer welk OpenAI-model elke feature gebruikt. Wijzigingen worden binnen 5 minuten actief (cache)."
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <SettingField settingKey="effie_model" label="Effie analyse"
          helperText="Model voor campagne-analyse (standaard: gpt-5.5)" />
        <SettingField settingKey="recorder_format_model" label="Meeting Recorder (formatting)"
          helperText="Model voor transcript-formatting (standaard: gpt-5.5)" />
        <SettingField settingKey="inspiration_model" label="Inspiratievideo's (analyse)"
          helperText="Model voor titel, tags en samenvatting (standaard: gpt-5.5)" />
        <SettingField settingKey="recorder_summary_model" label="Meeting Recorder (samenvatting)"
          helperText="Model voor titels en samenvattingen (standaard: gpt-5-mini)" />
        <SettingField settingKey="callsheet_model" label="Callsheets"
          helperText="Model voor weerbeschrijving op callsheets (standaard: gpt-5-mini)" />
        <SettingField settingKey="srt_model" label="Ondertitels"
          helperText="Model voor SRT-correctie (standaard: gpt-5-mini)" />
        <SettingField settingKey="ritmo_model" label="Ondertitel Studio (Ritmo)"
          helperText="Model voor het ritmisch groeperen van woorden (standaard: gpt-5-mini)" />
        <SettingField settingKey="offerte_model" label="Offerte-generator"
          helperText="Model voor opbouw en formulering van offertes (standaard: gpt-5.5)" />
        <SettingField settingKey="openai_model" label="Script Breakdown"
          helperText="Model voor scènes en shotlist (standaard: gpt-5.5)" />
        <SettingField settingKey="assistant_model" label="Frits (mail-assistent)"
          helperText="Model voor triage en concepten (standaard: gpt-5.5)" />
      </Box>

      <Box sx={{ mt: 4 }}>
        <SectionHeader
          title="Overige tools"
          subtitle="Modellen die eerst in de code vastlagen. Leeg of Reset = de standaard."
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {OVERIGE_MODELLEN.map((m) => (
          <SettingField key={m.key} settingKey={m.key} label={m.label}
            helperText={`${m.help} (standaard: ${m.standaard})`} />
        ))}
      </Box>

      <Box sx={{ mt: 4 }}>
        <SectionHeader
          title="Meeting-transcriptie (STT)"
          subtitle="Spraak-naar-tekst provider voor de FF Recorder, met vergelijkingsmodus."
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <SettingField settingKey="recorder_stt_provider" label="Transcriptie-provider"
          helperText="'fal' (standaard) of 'elevenlabs' — bepaalt de primaire transcriptie" />
        <SettingField settingKey="recorder_stt_compare_until" label="Vergelijkingsmodus t/m (YYYY-MM-DD)"
          helperText="Tot deze datum wordt elke opname door BEIDE providers getranscribeerd; de tweede verschijnt als aparte ⚖️-meeting. Leeg = uit." />
        <SettingField settingKey="recorder_stt_elevenlabs_model" label="ElevenLabs model"
          helperText="Scribe model-id (standaard: scribe_v1)" />
      </Box>
      <SttStatusCheck />
      <Modelcheck />
    </Box>
  );
}
